import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Image as ImageIcon, Search, HardDrive, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { mediaAPI } from '../../services/api';
import MediaUploader from '../../components/media/MediaUploader';
import MediaAssetCard from '../../components/media/MediaAssetCard';

/**
 * MediaLibrary — the lab's full image + asset manager.
 *
 * Handles:
 *   - Grid listing with folder filter + search + pagination
 *   - Drag-drop upload (via MediaUploader)
 *   - Storage quota bar with per-plan limits
 *   - Delete with confirmation
 *   - Empty states, error states, loading states
 *   - Refresh-after-upload so new assets appear immediately
 */

const FOLDERS = [
  { key: '', label: 'الكل' },
  { key: 'hero', label: 'Hero' },
  { key: 'sections', label: 'الأقسام' },
  { key: 'gallery', label: 'المعرض' },
  { key: 'blog', label: 'المدونة' },
  { key: 'offers', label: 'العروض' },
  { key: 'team', label: 'الفريق' },
  { key: 'logos', label: 'الشعارات' },
];

const PAGE_SIZE = 30;

function prettyBytes(b) {
  if (!b) return '0 B';
  if (b >= 1024 * 1024 * 1024) return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

export default function MediaLibrary() {
  const [folder, setFolder] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await mediaAPI.list({ folder, q, page, pageSize: PAGE_SIZE });
      const data = resp.data?.data || resp.data;
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setLoadError(err?.response?.data?.message || 'فشل تحميل المكتبة');
    } finally {
      setLoading(false);
    }
  }, [folder, q, page]);

  const fetchUsage = useCallback(async () => {
    try {
      const resp = await mediaAPI.usage();
      setUsage(resp.data?.data || resp.data);
    } catch (err) {
      // Usage is non-critical UI — don't block on it
      console.warn('Usage fetch failed', err);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const onAssetReady = useCallback((asset) => {
    // Prepend the new asset at the top; refresh usage bar
    setItems((prev) => [asset, ...prev.filter((a) => a._id !== asset._id)]);
    setTotal((t) => t + 1);
    fetchUsage();
  }, [fetchUsage]);

  const onDelete = useCallback(async (asset) => {
    setDeletingIds((prev) => new Set(prev).add(asset._id));
    try {
      await mediaAPI.remove(asset._id);
      setItems((prev) => prev.filter((a) => a._id !== asset._id));
      setTotal((t) => Math.max(0, t - 1));
      toast.success('تم الحذف');
      fetchUsage();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل الحذف');
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(asset._id);
        return next;
      });
    }
  }, [fetchUsage]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const usagePercent = usage?.unlimited ? 0 : Math.min(100, usage?.percentUsed || 0);
  const usageLabel = useMemo(() => {
    if (!usage) return '';
    if (usage.unlimited) return `${prettyBytes(usage.usedBytes)} • غير محدود`;
    return `${prettyBytes(usage.usedBytes)} من ${prettyBytes(usage.maxBytes)}`;
  }, [usage]);
  const usageColor = usagePercent >= 90 ? 'bg-blue-500' : usagePercent >= 70 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm">
                <ImageIcon className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800">مكتبة الوسائط</h1>
            </div>
            <p className="text-gray-500 text-sm mr-1">
              صور ووسائط موقعك التعريفي — تخزين سريع مع CDN عالمي
            </p>
          </div>
        </div>

        {/* Storage usage */}
        {usage && (
          <div className="mb-6 p-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700">مساحة التخزين المستخدمة</span>
              </div>
              <span className="text-xs text-gray-500">{usageLabel}</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full transition-all ${usageColor}`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
            {!usage.unlimited && usagePercent >= 90 && (
              <div className="mt-2 flex items-start gap-2 text-xs text-blue-600">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>اقتربت من الحد الأقصى — احذف ملفات أو ارقَّ لباقة أعلى.</span>
              </div>
            )}
          </div>
        )}

        {/* Uploader */}
        <div className="mb-6">
          <MediaUploader folder={folder || 'sections'} onAssetReady={onAssetReady} />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="بحث في أسماء الملفات والوسوم..."
              className="w-full pr-10 pl-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>
          <select
            value={folder}
            onChange={(e) => { setFolder(e.target.value); setPage(1); }}
            className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {FOLDERS.map((f) => (
              <option key={f.key || 'all'} value={f.key}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : loadError ? (
          <div className="p-8 rounded-2xl border border-blue-100 bg-blue-50 text-center">
            <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-blue-500" />
            <p className="text-sm text-blue-700">{loadError}</p>
            <button onClick={fetchItems} className="mt-3 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm">
              أعد المحاولة
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            <ImageIcon className="w-12 h-12 mx-auto mb-3" />
            <p className="text-sm">لا توجد وسائط بعد — ارفع أول صورة من الأعلى</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {items.map((asset) => (
                <MediaAssetCard
                  key={asset._id}
                  asset={asset}
                  onDelete={deletingIds.has(asset._id) ? undefined : onDelete}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white disabled:opacity-50"
                >السابق</button>
                <span className="text-sm text-gray-500 mx-1">صفحة {page} من {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 bg-white disabled:opacity-50"
                >التالي</button>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
